import { useEffect, useMemo, useRef, useState, type FocusEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ArrowLeft, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { OnAddLayer } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/constants';
import { AuthenticatedPortalItems } from 'components/MapPage/CorporateViewTabs/LayersTab/AuthenticatedPortalItems';
import { useCreateManyExternalLayersMutation } from 'hooks/externalLayer/useCreateManyExternalLayersMutation';
import { useCreateInternalLayerMutation } from 'hooks/internalLayer/useCreateInternalLayerMutation';
import { useUpdateInternalLayerMutation } from 'hooks/internalLayer/useUpdateInternalLayerMutation';
import { useReplaceLayerGrantsMutation } from 'hooks/internalLayer/useLayerGrants';
import type { InternalLayer, LayerGrant } from 'hooks/internalLayer/types';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';
import { fetchInternalLayerVersions } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import type { ScopedPortalItem } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisPortal';
import { LayerFeaturesEditor } from './LayerFeaturesEditor';
import { LayerFeaturesContent } from './LayerFeaturesContent';
import { useUscgMapContext } from './UscgMapContext';
import { useIsCommandCenter } from './utils/isCommandCenter';
import { IncidentSelect } from './IncidentSelect';
import type { CreateIcsIncidentLocationBody } from 'hooks/icsLocation/icsIncidentLocation.types';
import {
  selectGroup,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { InternalFeatureList } from './InternalFeatureList';
import { SitStatFeatureItem } from '../SitStatTab/SitStatFeatureItem';
import { CategorySelect } from './CategorySelect';
import { AddDhsLayer } from './AddDhsLayer';
import { AddDefaultLayer } from './AddDefaultLayer';
import { Row, Col } from 'react-bootstrap';
import { buildStoredFieldsGraphic } from './utils/focusFeatureOnMap';
import { locationStoredFields } from './utils/locationPopup';
import {
  registerDraftLayerLeaveGuard,
  requestDraftLayerLeave,
} from './draftLayerLeaveGuard';
import { InternalLayerVersionBanner } from './InternalLayerVersionBanner';
import { LayerPrivacyControl } from './LayerPrivacyControl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type PendingObject = {
  pendingGraphicId: string;
  location: CreateIcsIncidentLocationBody;
};

function formatDraftLastUpdated(
  updatedAt?: string | null,
  updatedByUserName?: string | null
) {
  if (!updatedAt) return null;
  const when = new Date(updatedAt);
  if (Number.isNaN(when.getTime())) return null;
  return `Last updated at ${when.toLocaleString()} by ${
    updatedByUserName?.trim() || 'Unknown'
  }`;
}

function readCreatedLayer(created: unknown): {
  layer: InternalLayer | null;
  version: InternalLayerVersion | null;
} {
  if (!created || typeof created !== 'object') {
    return { layer: null, version: null };
  }
  if (
    'layer' in created &&
    created.layer &&
    typeof created.layer === 'object'
  ) {
    const version =
      'version' in created &&
      created.version &&
      typeof created.version === 'object'
        ? (created.version as InternalLayerVersion)
        : null;
    return { layer: created.layer as InternalLayer, version };
  }
  if ('id' in created && typeof created.id === 'string') {
    return { layer: created as InternalLayer, version: null };
  }
  return { layer: null, version: null };
}

type AddLayerPanelProps = {
  onBack: () => void;
  layerType: Parameters<OnAddLayer>[0];
};

export function AddLayerPanel({ onBack, layerType }: AddLayerPanelProps) {
  const group = useAppSelector(selectGroup);
  const userGuid = useAppSelector(selectUserGuid);

  const createManyExternalLayers = useCreateManyExternalLayersMutation();
  const createInternalLayer = useCreateInternalLayerMutation();
  const updateInternalLayer = useUpdateInternalLayerMutation();
  const replaceLayerGrants = useReplaceLayerGrantsMutation();
  const isCommandCenter = useIsCommandCenter();
  const actualIncidentId = useActualIncidentId();
  const [incidentId, setIncidentId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<
    Map<string, ScopedPortalItem>
  >(new Map());

  const [pendingObjects, setPendingObjects] = useState<PendingObject[]>([]);
  const [objectSearch, setObjectSearch] = useState('');
  const [objectFormOpen, setObjectFormOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [savedLayer, setSavedLayer] = useState<InternalLayer | null>(null);
  const [savedName, setSavedName] = useState('');
  const [savedDescription, setSavedDescription] = useState('');
  const [draftOpenedAt, setDraftOpenedAt] = useState<string | null>(null);
  const [pendingGrants, setPendingGrants] = useState<LayerGrant[]>([]);
  const [editingField, setEditingField] = useState<
    'name' | 'description' | null
  >(null);
  const {
    focusFeatureOnMap,
    stopDrawing,
    clearPendingGraphics,
    removePendingGraphicsByPendingGraphicId,
    setPreviewLayers,
    setSelectedInternalLayerVersion,
    setInternalLayerVisible,
  } = useUscgMapContext();
  const stopDrawingRef = useRef(stopDrawing);
  const clearPendingGraphicsRef = useRef(clearPendingGraphics);
  const proceedRef = useRef<(() => void) | null>(null);
  const dirtyRef = useRef(false);
  stopDrawingRef.current = stopDrawing;
  clearPendingGraphicsRef.current = clearPendingGraphics;

  const handleBack = () => {
    if (layerType === 'internal') {
      requestDraftLayerLeave(onBack);
      return;
    }
    stopDrawing();
    clearPendingGraphics();
    onBack();
  };

  const discardLatestPendingObject = () => {
    if (pendingObjects.length === 0) return;

    const last = pendingObjects[pendingObjects.length - 1];
    removePendingGraphicsByPendingGraphicId(last.pendingGraphicId);
    setPendingObjects((prev) => prev.slice(0, -1));
  };

  const internalLayerForm = useForm({
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const internalLayerName = useWatch({
    control: internalLayerForm.control,
    name: 'name',
    defaultValue: '',
  });
  const internalLayerDescription = useWatch({
    control: internalLayerForm.control,
    name: 'description',
    defaultValue: '',
  });

  const nameDirty = (internalLayerName ?? '') !== savedName;
  const descriptionDirty =
    (internalLayerDescription ?? '') !== savedDescription;
  const draftIsDirty =
    layerType === 'internal' &&
    (nameDirty ||
      descriptionDirty ||
      pendingObjects.length > 0 ||
      objectFormOpen);
  dirtyRef.current = draftIsDirty;

  const savingDraft =
    createInternalLayer.isPending ||
    updateInternalLayer.isPending ||
    replaceLayerGrants.isPending;
  const { data: roster = [] } = useGetRostersForGroup(group?.group_guid ?? '');
  const draftAuthor = roster.find((person) => person.user_guid === userGuid);
  const draftAuthorName =
    draftAuthor?.user_name || draftAuthor?.email_address || null;
  const draftBanner = formatDraftLastUpdated(draftOpenedAt, draftAuthorName);

  useEffect(() => {
    if (layerType !== 'internal') return;
    setDraftOpenedAt((current) => current ?? new Date().toISOString());
    registerDraftLayerLeaveGuard((proceed) => {
      const finish = () => {
        stopDrawingRef.current();
        clearPendingGraphicsRef.current();
        proceed();
      };
      if (!dirtyRef.current) {
        finish();
        return;
      }
      proceedRef.current = finish;
      setLeaveOpen(true);
    });
    return () => registerDraftLayerLeaveGuard(null);
  }, [layerType]);

  useEffect(() => {
    if (!draftIsDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [draftIsDirty]);

  const discardLeave = () => {
    const proceed = proceedRef.current;
    proceedRef.current = null;
    setLeaveOpen(false);
    proceed?.();
  };

  const selectedItemIds = useMemo(
    () => new Set(selectedItems.keys()),
    [selectedItems]
  );

  const isImporting = createManyExternalLayers.isPending;
  const canImport = selectedItemIds.size > 0 && !isImporting;
  const groupGuid = group?.group_guid;

  const handleToggleItem = (item: ScopedPortalItem, checked: boolean) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(item.id!, item);
      } else {
        next.delete(item.id!);
      }
      return next;
    });
  };

  const handleToggleAllItems = (
    items: ScopedPortalItem[],
    checked: boolean
  ) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      for (const item of items) {
        if (checked) {
          next.set(item.id!, item);
        } else {
          next.delete(item.id!);
        }
      }
      return next;
    });
  };

  const handleImport = async () => {
    const itemsToImport = Array.from(selectedItems.values());

    const effectiveIncidentId = isCommandCenter ? incidentId : actualIncidentId;

    await createManyExternalLayers.mutateAsync({
      groupGuid,
      targetFolderId: selectedFolderId ?? null,
      layers: itemsToImport.map((item) => ({
        incidentId: effectiveIncidentId || null,
        arcgisItemId: item.id,
        serviceUrl: item.url,
        itemType: item.type,
        name: item.title ?? 'Untitled item',
        description: null,
        userGuid: item.userScoped ? userGuid : null,
        authType: layerType === 'external' ? 'userAuth' : 'apiKey',
      })),
    });

    setPreviewLayers([]);
    setSelectedItems(new Map());
    setSelectedFolderId(null);
    handleBack();
  };

  const leaveFieldEdit = () => {
    setEditingField(null);
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  };

  const selectNewestVersion = async (layerId: string) => {
    const versions = await fetchInternalLayerVersions({
      filter: { internalLayerId: layerId },
    });
    const newest = versions.data.reduce<InternalLayerVersion | null>(
      (best, version) => {
        if (!best || version.versionNumber > best.versionNumber) return version;
        return best;
      },
      null
    );
    if (newest) setSelectedInternalLayerVersion(layerId, newest);
  };

  const persistSavedField = async (intent: 'name' | 'description') => {
    if (!savedLayer) return;
    if (intent === 'name') {
      const name = internalLayerForm.getValues('name').trim();
      const updated = await updateInternalLayer.mutateAsync({
        internalLayerId: savedLayer.id,
        name,
      });
      setSavedName(name);
      internalLayerForm.setValue('name', name);
      setSavedLayer(updated);
    } else {
      const description =
        internalLayerForm.getValues('description').trim() || null;
      const updated = await updateInternalLayer.mutateAsync({
        internalLayerId: savedLayer.id,
        description,
      });
      setSavedDescription(description ?? '');
      internalLayerForm.setValue('description', description ?? '');
      setSavedLayer(updated);
    }
    await selectNewestVersion(savedLayer.id);
    leaveFieldEdit();
  };

  const requestFieldSave = (intent: 'name' | 'description') => {
    const name = internalLayerForm.getValues('name').trim();
    if (!name) {
      internalLayerForm.setError('name', { message: 'Name is required.' });
      return;
    }
    internalLayerForm.clearErrors('name');
    if (!groupGuid) return;
    if (!savedLayer) {
      void createLayer(intent, pendingGrants);
      return;
    }
    void persistSavedField(intent);
  };

  const keepFieldEditing = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: 'name' | 'description'
  ) => {
    const next = event.relatedTarget;
    return (
      next instanceof Element &&
      Boolean(next.closest(`[data-field-actions="${field}"]`))
    );
  };

  const cancelField = (intent: 'name' | 'description') => {
    if (intent === 'name') {
      internalLayerForm.setValue('name', savedName);
      internalLayerForm.clearErrors('name');
    } else {
      internalLayerForm.setValue('description', savedDescription);
    }
  };

  const createLayer = async (
    intent: 'name' | 'description',
    grants: LayerGrant[]
  ) => {
    if (!groupGuid || savedLayer) return;
    const name = internalLayerForm.getValues('name').trim();
    if (!name) return;
    const description =
      intent === 'description'
        ? internalLayerForm.getValues('description').trim() || null
        : savedDescription.trim() || null;

    const created = await createInternalLayer.mutateAsync({
      groupGuid,
      incidentId: actualIncidentId || null,
      name,
      description,
      defaultVisible: true,
      icsIncidentLocations: pendingObjects.map(({ location }) => ({
        name: location.name,
        description: location.description,
        geometry: location.geometry,
        resourceType: location.resourceType,
        properties: location.properties,
      })),
    });

    const { layer, version } = readCreatedLayer(created);
    if (layer) {
      if (grants.length > 0) {
        try {
          await replaceLayerGrants.mutateAsync({
            internalLayerId: layer.id,
            grants,
            quiet: true,
          });
        } catch {
          // The layer is already saved. The grants mutation reports its own error.
        }
      }
      if (version) setSelectedInternalLayerVersion(layer.id, version);
      setInternalLayerVisible(layer.id, true);
      setSavedLayer(layer);
    }

    setSavedName(name);
    setSavedDescription(description ?? '');
    internalLayerForm.setValue('name', name);
    internalLayerForm.setValue('description', description ?? '');
    setPendingObjects([]);
    dirtyRef.current = false;
    clearPendingGraphics();
    stopDrawing();
    leaveFieldEdit();
  };

  const nameField = internalLayerForm.register('name', {
    required: 'Name is required.',
  });
  const descriptionField = internalLayerForm.register('description');

  const objectQuery = objectSearch.trim().toLowerCase();
  const visiblePendingObjects = pendingObjects.filter((feature) => {
    if (!objectQuery) return true;
    const name = feature.location.name?.toLowerCase() ?? '';
    const description = feature.location.description?.toLowerCase() ?? '';
    return name.includes(objectQuery) || description.includes(objectQuery);
  });

  const headerText = (() => {
    if (layerType === 'external') return 'Add layer (external)';
    if (layerType === 'internal') return 'Draft Map Layer (PRATUS Native)';
    if (layerType === 'dhs') return 'Add layer (DHS)';
    if (layerType === 'default') return 'Add Default Layer';
  })();

  return (
    <div className="d-flex flex-column gap-3 p-2">
      {layerType !== 'internal' ? (
        <div className="dark tw:flex tw:items-center tw:gap-2 tw:text-foreground">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handleBack}
            aria-label="Back to layers"
          >
            <ArrowLeft className="tw:size-4" />
          </Button>
          <h5 className="fw-semibold my-0">{headerText}</h5>
        </div>
      ) : null}
      <div style={{ marginLeft: '30px' }}>
        {layerType === 'external' && (
          <AuthenticatedPortalItems
            selectedItemIds={selectedItemIds}
            onToggleItem={handleToggleItem}
            onToggleAllItems={handleToggleAllItems}
          />
        )}
        {layerType === 'dhs' && (
          <AddDhsLayer
            selectedItemIds={selectedItemIds}
            onToggleItem={handleToggleItem}
            onToggleAllItems={handleToggleAllItems}
          />
        )}
        {layerType === 'default' && (
          <AddDefaultLayer
            selectedItemIds={selectedItemIds}
            onToggleItem={handleToggleItem}
            onToggleAllItems={handleToggleAllItems}
          />
        )}
        {['dhs', 'external', 'default'].includes(layerType) && (
          <div className="d-flex flex-column mt-3 gap-3">
            <Row>
              <Col>
                <IncidentSelect
                  value={isCommandCenter ? incidentId : actualIncidentId}
                  onChange={setIncidentId}
                  isDisabled={!isCommandCenter}
                  isClearable
                />
              </Col>
              <Col>
                <CategorySelect
                  label="Category"
                  value={selectedFolderId}
                  onChange={setSelectedFolderId}
                />
              </Col>
            </Row>
            <StylishNewButton
              type="button"
              className="button--primary"
              onClick={handleImport}
              disabled={!canImport}
            >
              {isImporting ? 'Importing…' : 'Import'}
            </StylishNewButton>
          </div>
        )}
      </div>

      {layerType === 'internal' && (
        <div className="dark tw:flex tw:flex-col tw:gap-3 tw:text-foreground">
          {savedLayer ? (
            <InternalLayerVersionBanner layer={savedLayer} />
          ) : (
            <div className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-emerald-500 tw:bg-emerald-500/10 tw:px-3 tw:py-2 tw:text-left tw:text-xs tw:leading-snug tw:font-normal tw:text-emerald-200">
              <span className="tw:min-w-0 tw:flex-1 tw:whitespace-normal">
                {draftBanner}
              </span>
              <LayerPrivacyControl
                groupGuid={groupGuid ?? ''}
                incidentId={isCommandCenter ? null : (actualIncidentId ?? null)}
                scope={isCommandCenter ? 'hub' : 'incident'}
                grants={pendingGrants}
                onGrantsChange={setPendingGrants}
              />
            </div>
          )}
          <form
            id="add-internal-layer-form"
            onSubmit={(event) => event.preventDefault()}
          >
            <FieldGroup className="tw:gap-4">
              <Field>
                <FieldLabel htmlFor="add-internal-layer-name">
                  Name *
                </FieldLabel>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Input
                    id="add-internal-layer-name"
                    className="tw:min-w-0 tw:flex-1"
                    aria-invalid={!!internalLayerForm.formState.errors.name}
                    {...nameField}
                    onFocus={() => setEditingField('name')}
                    onBlur={(event) => {
                      void nameField.onBlur(event);
                      if (keepFieldEditing(event, 'name')) return;
                      setEditingField((current) =>
                        current === 'name' ? null : current
                      );
                    }}
                  />
                  {editingField === 'name' ? (
                    <div
                      data-field-actions="name"
                      className="tw:flex tw:shrink-0 tw:items-center tw:gap-1"
                      onMouseDown={(event) => event.preventDefault()}
                    >
                      <Button
                        type="button"
                        size="icon"
                        className="tw:h-9 tw:w-9"
                        aria-label="Save name"
                        disabled={savingDraft}
                        onClick={() => requestFieldSave('name')}
                      >
                        <Check />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="tw:h-9 tw:w-9"
                        aria-label="Cancel name"
                        disabled={savingDraft}
                        onClick={() => cancelField('name')}
                      >
                        <X />
                      </Button>
                    </div>
                  ) : null}
                </div>
                <FieldError
                  errors={[internalLayerForm.formState.errors.name]}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="add-internal-layer-description">
                  Description
                </FieldLabel>
                <Textarea
                  id="add-internal-layer-description"
                  className="tw:w-full"
                  placeholder="Layer description"
                  rows={4}
                  {...descriptionField}
                  onFocus={() => setEditingField('description')}
                  onBlur={(event) => {
                    void descriptionField.onBlur(event);
                    if (keepFieldEditing(event, 'description')) return;
                    setEditingField((current) =>
                      current === 'description' ? null : current
                    );
                  }}
                />
                {editingField === 'description' ? (
                  <div
                    data-field-actions="description"
                    className="tw:flex tw:justify-end tw:gap-2"
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    <Button
                      type="button"
                      size="sm"
                      disabled={savingDraft}
                      onClick={() => requestFieldSave('description')}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={savingDraft}
                      onClick={() => cancelField('description')}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : null}
              </Field>
            </FieldGroup>
          </form>
          <div>
            {savedLayer ? (
              <LayerFeaturesContent
                layer={savedLayer}
                layerType="internal"
                showAddObjectButton
              />
            ) : (
              <>
                <LayerFeaturesEditor
                  showAddObjectButton={true}
                  showSearch
                  searchTerm={objectSearch}
                  onChangeSearchTerm={setObjectSearch}
                  onEditingChange={setObjectFormOpen}
                  onSubmitLocation={async ({ location, pendingGraphicId }) => {
                    if (!pendingGraphicId) return;
                    setPendingObjects((prev) => [
                      ...prev,
                      { pendingGraphicId, location },
                    ]);
                  }}
                  onCancelLocation={discardLatestPendingObject}
                />
                <div className="tw:mt-3">
                  <InternalFeatureList
                    FeatureItemComponent={SitStatFeatureItem}
                    features={visiblePendingObjects.map((feature) => ({
                      key: feature.pendingGraphicId,
                      featureId: feature.pendingGraphicId,
                      title: feature.location.name,
                      resourceType: feature.location.resourceType,
                      properties: feature.location.properties,
                      updatedAt: null,
                      geometry: feature.location.geometry,
                      fields: locationStoredFields(feature.location),
                    }))}
                    onDelete={(featureId) => {
                      removePendingGraphicsByPendingGraphicId(featureId);
                      setPendingObjects((prev) =>
                        prev.filter(
                          (item) => item.pendingGraphicId !== featureId
                        )
                      );
                    }}
                    onFocusFeature={(feature) => {
                      const graphic = buildStoredFieldsGraphic({
                        geometry: feature.geometry,
                        title: feature.title,
                        fields: feature.fields ?? [],
                      });
                      if (graphic) void focusFeatureOnMap({ graphic });
                    }}
                  />
                </div>
                {objectQuery &&
                pendingObjects.length > 0 &&
                visiblePendingObjects.length === 0 ? (
                  <p className="tw:mt-2 tw:mb-0 tw:text-xs tw:text-muted-foreground">
                    No objects match.
                  </p>
                ) : null}
              </>
            )}
          </div>
          <AlertDialog
            open={leaveOpen}
            onOpenChange={(open) => {
              if (open) return;
              proceedRef.current = null;
              setLeaveOpen(false);
            }}
          >
            <AlertDialogContent className="dark">
              <AlertDialogHeader>
                <AlertDialogTitle>Leave this draft?</AlertDialogTitle>
                <AlertDialogDescription>
                  Progress will be lost unless you save the draft.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep editing</AlertDialogCancel>
                <AlertDialogAction onClick={discardLeave}>
                  Discard
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}
